import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  IOperatingModel,
  IProcess,
  allOPModels,
  allRoles,
  IMilestone,
  ITraining,
  IProcessMap,
  IPhase,
} from 'app/types/Shared';
import {omApi} from './operatingModelApi';
import {RootState} from '..';

export interface IOMState {
  allOMs?: Array<allOPModels>;
  allTrainings?: Array<ITraining>;
  allRoles?: allRoles;
  allProcesses?: Array<IProcess>;
  allMaps?: Array<IProcessMap>;
  allPhases?: Array<IPhase>;
  milestones?: Array<IMilestone>;
  currentOM?: IOperatingModel;
  storeInitalized: boolean;
  processesMultipleModels?: Array<{process_id: string; name: string; model?: string}>;
}

export const initialState: IOMState = {
  allOMs: undefined,
  allTrainings: undefined,
  allRoles: undefined,
  allProcesses: undefined,
  allMaps: undefined,
  milestones: undefined,
  currentOM: undefined,
  storeInitalized: false,
  allPhases: undefined,
  processesMultipleModels: [],
};

export const opModelSlice = createSlice({
  name: 'opModel',
  initialState,
  reducers: {
    setAllModels: (state, {payload}: PayloadAction<Array<allOPModels>>) => {
      state.allOMs = payload;
    },
    setAllTrainings: (state, {payload}: PayloadAction<Array<ITraining>>) => {
      state.allTrainings = payload;
    },
    setAllProcesses: (state, {payload}: PayloadAction<Array<IProcess>>) => {
      state.allProcesses = payload;
    },
    setAllMaps: (state, {payload}: PayloadAction<Array<IProcessMap>>) => {
      state.allMaps = payload;
    },
    setMilestones: (state, {payload}: PayloadAction<Array<IMilestone>>) => {
      state.milestones = payload;
    },
    setSelectedModel: (state, {payload}: PayloadAction<IOperatingModel>) => {
      state.currentOM = payload;
    },
    setStoreInitalized: (state, {payload}: PayloadAction<boolean>) => {
      state.storeInitalized = payload;
    },
    setAllPhases: (state, {payload}: PayloadAction<Array<IPhase>>) => {
      state.allPhases = payload;
    },
    setAllRoles: (state, {payload}: PayloadAction<allRoles>) => {
      state.allRoles = payload;
    },
    setProcessesWithMultipleModels: (state, {payload}: PayloadAction<Array<IProcess>>) => {
      const multipleModels = payload.filter((x) => payload.filter((y) => x.name === y.name)?.length > 1);

      state.processesMultipleModels = multipleModels.map((p) => {
        return {process_id: p.process_id, name: p.name, model: p.operating_models_list};
      });
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(omApi.endpoints.getAllOpModels.matchFulfilled, (state, {payload}) => {
      console.log('All Ops Models query result: ' + payload?.length + ' models');
      state.allOMs = payload;
    });

    builder.addMatcher(omApi.endpoints.getOpModelById.matchFulfilled, (state, {payload}) => {
      // console.log('Get current opModel ' + payload);
      state.currentOM = payload;
    });

    builder.addMatcher(omApi.endpoints.getMilestones.matchFulfilled, (state, {payload}) => {
      state.milestones = payload;
    });

    builder.addMatcher(omApi.endpoints.getAllRoles.matchFulfilled, (state, {payload}) => {
      state.allRoles = payload;
    });

    builder.addMatcher(omApi.endpoints.getAllProcesses.matchFulfilled, (state, {payload}) => {
      state.allProcesses = payload;
    });

    builder.addMatcher(omApi.endpoints.getAllTrainings.matchFulfilled, (state, {payload}) => {
      state.allTrainings = payload;
    });
    builder.addMatcher(omApi.endpoints.getAllMaps.matchFulfilled, (state, {payload}) => {
      state.allMaps = payload;
    });
    builder.addMatcher(omApi.endpoints.getAllPhases.matchFulfilled, (state, {payload}) => {
      state.allPhases = payload;
    });
  },
});

export const {
  setAllModels,
  setAllTrainings,
  setMilestones,
  setStoreInitalized,
  setAllProcesses,
  setSelectedModel,
  setAllMaps,
  setAllPhases,
  setAllRoles,
  setProcessesWithMultipleModels,
} = opModelSlice.actions;

export const selectCurrentModel = (state: RootState) => state.operatingModel.currentOM;
// export const selectAllPhases = (state: RootState) => state.operatingModel.currentOM?.phases;
export const selectAllModels = (state: RootState) => state.operatingModel.allOMs;
export const selectAllProcesses = (state: RootState) => state.operatingModel.allProcesses;
export const selectAllRoles = (state: RootState) => state.operatingModel.allRoles;
export const selectAllTrainings = (state: RootState) => state.operatingModel.allTrainings;
export const selectAllMaps = (state: RootState) => state.operatingModel.allMaps;
export const selectMilestones = (state: RootState) => state.operatingModel.milestones;
export const selectStoreInitalized = (state: RootState) => state.operatingModel.storeInitalized;
export const selectAllPhases = (state: RootState) => state.operatingModel.allPhases;
export const selectProcessesWithMultipleModels = (state: RootState) => state.operatingModel.processesMultipleModels;

export default opModelSlice.reducer;
